'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, CheckCircle, Lightbulb } from 'lucide-react'

export default function WetsuitPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-green-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/equipment"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          장비 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              프리다이빙 웻슈트 가이드
            </h1>
            <p className={`mt-2 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              체온 유지와 부력 조절을 위한 웻슈트 선택 가이드입니다.
            </p>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 웻슈트의 역할 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                웻슈트의 역할
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { emoji: '🌡️', title: '체온 유지', desc: '저체온증 예방' },
                  { emoji: '🏊', title: '부력 제공', desc: '수영 효율 증가' },
                  { emoji: '🛡️', title: '보호', desc: '해파리, 바위 등으로부터 보호' },
                  { emoji: '⚡', title: '저항 감소', desc: '유선형 체형 형성' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg">{item.emoji}</span>
                    <span className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </span>
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      : {item.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 슈트 두께 선택 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                슈트 두께 선택
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                {[
                  {
                    title: '1mm - 2mm (여름용)',
                    items: ['수온: 26°C 이상', '계절: 한여름 바다', '특징: 가볍고 움직임 자유로움', '가격: 20-40만원']
                  },
                  {
                    title: '3mm (봄/가을용)',
                    items: ['수온: 20-26°C', '계절: 봄, 가을, 실내 풀', '특징: 가장 범용적', '가격: 30-60만원', '추천: 첫 슈트로 최적']
                  },
                  {
                    title: '5mm (겨울용)',
                    items: ['수온: 15-20°C', '계절: 늦가을, 겨울', '특징: 두껍고 따뜻함', '가격: 40-80만원', '주의: 부력 증가로 웨이트 필요']
                  },
                  {
                    title: '7mm (극한 환경)',
                    items: ['수온: 15°C 이하', '계절: 한겨울', '특징: 최대 보온', '가격: 50-100만원', '특수 환경용']
                  },
                ].map((section, idx) => (
                  <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      {section.title}
                    </h3>
                    <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-1.5">
                          <CheckCircle className="w-3 h-3 text-blue-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 슈트 유형 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                슈트 유형
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xxs:gap-3">
                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                    투피스 (Two-Piece)
                  </h3>
                  <p className={`text-sm xxs:text-xs mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    상의(재킷)와 하의(팬츠)가 분리된 형태
                  </p>
                  <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    장점: 착탈 쉬움, 온도 조절 가능<br />
                    단점: 복부 부위 겹침으로 부력 증가
                  </p>
                </div>
                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                    원피스 (One-Piece)
                  </h3>
                  <p className={`text-sm xxs:text-xs mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    전신이 하나로 연결된 형태
                  </p>
                  <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    장점: 매끈한 외형, 부력 균일<br />
                    단점: 착탈 어려움, 화장실 불편
                  </p>
                </div>
              </div>
            </div>

            {/* 웻슈트 선택 팁 */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <div className="flex items-start gap-2">
                <Lightbulb className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    웻슈트 선택 팁
                  </h3>
                  <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-yellow-200/80' : 'text-yellow-700'}`}>
                    <li>• <strong>피팅이 가장 중요</strong>: 몸에 꼭 맞아야 보온 효과</li>
                    <li>• 너무 타이트하면 호흡 방해</li>
                    <li>• 너무 헐렁하면 물이 들어와 차가움</li>
                    <li>• 팔, 다리, 목 부위 확인</li>
                    <li>• 가능하면 커스텀 제작 추천</li>
                    <li>• 3mm 슈트가 가장 범용적</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 슈트 관리 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                슈트 관리
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { title: '착용 시', desc: '손톱 조심, 천천히 입기. 샴푸물로 미끄럽게 하면 쉬움' },
                  { title: '사용 후', desc: '민물로 안팎 헹구기. 뒤집어서 그늘에 건조' },
                  { title: '보관', desc: '접지 말고 전용 옷걸이 사용. 직사광선 피하기' },
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 xxs:p-2 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <p className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </p>
                    <p className={`text-xs xxs:text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
